import type { FC, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useLocation } from "react-router";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { partnerByIdQueryOptions, selectPartnerOptionsQueryOptions, partnerFormQueryOptions } from "../../api/partners";
import { prefetch } from "../../api/prefetch";
import { prefetchSystemById, systemSelectOptionsQueryOptions, useGetSystemForm } from "../../api/systems";
import { PARTNER_FORM_TYPE, SYSTEM_FORM_TYPE } from "../../api/assistant";
import { prefetchRegistrationById, useGetRegistrationForm } from "../../api/registrations";
import type { Form, QuestionCard } from "../../dynamicForm/types";
import { PartnerIcon3, RegistrationControllerIcon3, SystemIcon3 } from "../../shared/icons";
import { withQueryParams } from "../../shared/navigateWithQueryParams";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { useAssistantApi } from "./AssistantApiContext";

type RecordKind = "registration" | "system" | "partner";

const kindOf = (formType: string): RecordKind => {
  const normalised = formType.toLowerCase();
  if (normalised === SYSTEM_FORM_TYPE) return "system";
  if (normalised === PARTNER_FORM_TYPE) return "partner";
  return "registration";
};

const useRecordTarget = (formType: string, recordId: string) => {
  const { paths } = usePrivacyNavigate();
  const { search } = useLocation();
  const kind = kindOf(formType);
  const systems = useQuery({ ...systemSelectOptionsQueryOptions(), throwOnError: false, enabled: kind === "system" });
  const partners = useQuery({
    ...selectPartnerOptionsQueryOptions(),
    throwOnError: false,
    enabled: kind === "partner",
  });
  const listedName =
    kind === "system"
      ? systems.data?.find(option => option.id === recordId)?.name
      : kind === "partner"
        ? partners.data?.find(option => option.id === recordId)?.name
        : undefined;
  const pathId = encodeURIComponent(recordId);
  const path =
    kind === "system"
      ? paths.systemsInline(pathId)
      : kind === "partner"
        ? paths.partnersInline(pathId)
        : paths.registrationsInline({ id: pathId, variant: "Controller" });
  const isLoadingName = (kind === "system" && systems.isPending) || (kind === "partner" && partners.isPending);

  const prefetchRecord = () => {
    if (kind === "system") return void prefetchSystemById(recordId);
    if (kind === "partner") return void prefetch(partnerByIdQueryOptions(recordId));
    return void prefetchRegistrationById(recordId, "Controller");
  };

  return { kind, listedName, isLoadingName, to: withQueryParams(path, search), prefetchRecord };
};

const KIND_LABELS = {
  registration: "Assistant.FormType.Registration",
  system: "Assistant.FormType.System",
  partner: "Assistant.FormType.Partner",
} as const;

const KIND_ICONS = { registration: RegistrationControllerIcon3, system: SystemIcon3, partner: PartnerIcon3 } as const;

const CARD_FRAME = {
  display: "flex",
  gap: 1.5,
  alignItems: "flex-start",
  textAlign: "left",
  p: 1.5,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

export const AssistantRecordCardSkeleton: FC = () => (
  <Box sx={CARD_FRAME} aria-hidden="true">
    <Skeleton variant="rounded" width={18} height={18} sx={{ mt: 0.25, flexShrink: 0 }} />
    <Stack sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
      <Skeleton variant="text" width="65%" />
      <Skeleton variant="text" width="45%" sx={{ fontSize: theme => theme.typography.pxToRem(12) }} />
    </Stack>
  </Box>
);

export const AssistantRecordCards: FC<{ children?: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(14rem, 1fr))",
      gap: 1,
      my: 0.5,
    }}
  >
    {children}
  </Box>
);

type RecordCardProps = { formType: string; recordId: string; name: ReactNode; status?: string; owner?: string };

export const AssistantRecordCard: FC<RecordCardProps> = props => {
  const { isInvite } = useAssistantApi();

  return isInvite ? (
    <Box sx={CARD_FRAME}>
      <RecordCardBody kind={kindOf(props.formType)} name={props.name} status={props.status} owner={props.owner} />
    </Box>
  ) : (
    <LinkedRecordCard {...props} />
  );
};

const LinkedRecordCard: FC<RecordCardProps> = ({ formType, recordId, name, status, owner }) => {
  const { kind, listedName, isLoadingName, to, prefetchRecord } = useRecordTarget(formType, recordId);

  return (
    <Box
      component={RouterLink}
      to={to}
      onMouseEnter={prefetchRecord}
      onFocus={prefetchRecord}
      sx={{
        ...CARD_FRAME,
        "cursor": "pointer",
        "font": "inherit",
        "color": "inherit",
        "textDecoration": "none",
        "transition": "border-color 120ms, background-color 120ms",
        "&:hover": { borderColor: theme => theme.palette.primary.main, bgcolor: theme => theme.palette.grey[50] },
        "&:focus-visible": { outline: theme => `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
      }}
    >
      <RecordCardBody
        kind={kind}
        name={isLoadingName && !name ? <AssistantComponentSkeleton width={120} /> : (listedName ?? name)}
        status={status}
        owner={owner}
      />
    </Box>
  );
};

const RecordCardBody: FC<{ kind: RecordKind; name: ReactNode; status?: string; owner?: string }> = ({
  kind,
  name,
  status,
  owner,
}) => {
  const { t } = useTranslation();
  const Icon = KIND_ICONS[kind];
  const details = [status, owner].filter(Boolean).join(" · ");

  return (
    <>
      <Box sx={{ color: theme => theme.palette.text.secondary, pt: 0.25 }}>
        <Icon size={18} />
      </Box>
      <Stack sx={{ minWidth: 0, gap: 0.25 }}>
        <Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{name}</Typography>
        <Typography
          sx={{ color: theme => theme.palette.text.tertiary, fontSize: theme => theme.typography.pxToRem(12) }}
        >
          {details ? `${t(KIND_LABELS[kind])} · ${details}` : t(KIND_LABELS[kind])}
        </Typography>
      </Stack>
    </>
  );
};

type RecordLinkProps = { formType: string; recordId: string; name: ReactNode };

export const AssistantRecordLink: FC<RecordLinkProps> = props => {
  const { isInvite } = useAssistantApi();

  return isInvite ? props.name : <LinkedRecord {...props} />;
};

const LinkedRecord: FC<RecordLinkProps> = ({ formType, recordId, name }) => {
  const { listedName, isLoadingName, to, prefetchRecord } = useRecordTarget(formType, recordId);

  if (isLoadingName && !name) return <AssistantComponentSkeleton />;

  return (
    <Link
      component={RouterLink}
      to={to}
      onMouseEnter={prefetchRecord}
      onFocus={prefetchRecord}
      sx={{ verticalAlign: "baseline", font: "inherit" }}
    >
      {listedName ?? name}
    </Link>
  );
};

const questionIn = (form: Form | undefined, questionId: string): QuestionCard | undefined => {
  const search = (cards: Array<QuestionCard>): QuestionCard | undefined => {
    for (const card of cards) {
      if (card.id === questionId) return card;
      const inFollowUp = search((card.followUps ?? []).map(followUp => followUp.questionCard));
      if (inFollowUp) return inFollowUp;
    }
    return undefined;
  };

  return search((form?.sections ?? []).flatMap(section => section.questionCards));
};

export const AssistantComponentSkeleton: FC<{ width?: number }> = ({ width = 96 }) => (
  <Skeleton variant="text" width={width} sx={{ display: "inline-block", verticalAlign: "middle" }} />
);

const QuestionLabel: FC<{ form: Form | undefined; questionId: string }> = ({ form, questionId }) => {
  if (!form) return <AssistantComponentSkeleton width={140} />;

  const question = questionIn(form, questionId);

  return question ? (
    <Box component="q" sx={{ fontStyle: "italic" }}>
      {question.header}
    </Box>
  ) : null;
};

const RegistrationQuestion: FC<{ questionId: string }> = ({ questionId }) => {
  const { data } = useGetRegistrationForm("Controller");
  return <QuestionLabel form={data} questionId={questionId} />;
};

const SystemQuestion: FC<{ questionId: string }> = ({ questionId }) => {
  const { data } = useGetSystemForm();
  return <QuestionLabel form={data} questionId={questionId} />;
};

const PartnerQuestion: FC<{ questionId: string }> = ({ questionId }) => {
  const { data } = useQuery({ ...partnerFormQueryOptions(), throwOnError: false });
  return <QuestionLabel form={data} questionId={questionId} />;
};

export const AssistantQuestionReference: FC<{ formType: string; questionId: string }> = ({ formType, questionId }) => {
  const { isInvite } = useAssistantApi();
  const kind = kindOf(formType);
  if (isInvite) return null;
  if (kind === "system") return <SystemQuestion questionId={questionId} />;
  if (kind === "partner") return <PartnerQuestion questionId={questionId} />;
  return <RegistrationQuestion questionId={questionId} />;
};

export const AssistantCallout: FC<{ warning: boolean; children?: ReactNode }> = ({ warning, children }) => (
  <Alert severity={warning ? "warning" : "info"} sx={{ "& .MuiAlert-message > p": { m: 0 } }}>
    {children}
  </Alert>
);
